import React, { useMemo } from 'react';
import { Modal } from '@crisellevaldez/speculo-ui';
import { ContactHeader } from '../../calls/ContactHeader';
import { formatDate } from '../../../../utils/dateTime';
import { emailSrcDoc } from '../../../../utils/email/emailSrcDoc';
import { useOrganizationStore } from '../../../../stores/useOrganizationStore';
import type { GQLMessageItem } from '../../../../hooks/graphql/contacts/useContact';

interface EmailPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  email: GQLMessageItem | null;
  /** Contact display name for the To row (Remi-sent) / From row (inbound). */
  contactName?: string;
  /** Contact email — when known (e.g. RecentActivities exposes
   *  `contactEmail`), shown alongside the contact name. Falls back to
   *  the message's fromAddress/toAddress otherwise. */
  contactEmail?: string;
  /** With `onContactClick`, the header name opens the contact (closing the preview). */
  contactId?: string;
  onContactClick?: (contactId: string) => void;
}

/**
 * Email viewer modal — renders a stored message in a layout that reads
 * like a real email client: the lead in the header, the subject and send
 * time above the body, then the sanitized bodyHtml inside
 * a sandboxed iframe so the email's own CSS can't bleed into the app.
 */
export const EmailPreviewModal: React.FC<EmailPreviewModalProps> = ({
  isOpen,
  onClose,
  email,
  contactName,
  contactEmail,
  contactId,
  onContactClick,
}) => {
  const organization = useOrganizationStore((s) => s.organization);

  // Sandbox the email body in a srcdoc iframe so its own styles can't
  // affect the rest of the app. Emails are designed for a white canvas,
  // so we keep the iframe light regardless of the app's theme — forcing
  // dark inside the iframe inverted text colors but left email-built
  // containers (property cards, etc.) still white, producing light-on-
  // light unreadable rows.
  const srcDoc = useMemo(() => emailSrcDoc(email?.bodyHtml, email?.body), [email]);

  if (!email) return null;

  const sentAt = email.sentAt || email.deliveredAt || email.openedAt || email.createdAt;
  const timezone = organization?.timezone;
  // For outbound emails the lead is the recipient; for inbound the lead
  // is the sender. Decorate the right side with the contact's name.
  const isInbound = email.direction === 'inbound';
  const leadName = contactName || null;
  const leadEmail = contactEmail || (isInbound ? email.fromAddress : email.toAddress) || null;

  // Subject fallback chain — `subject` is often empty on certain message
  // sources, so use the template name when present, then strip a short
  // snippet from the body so the modal never headlines with "(no subject)"
  // when there's clearly real content underneath.
  const subjectDisplay =
    email.subject?.trim() ||
    (() => {
      const raw = email.bodyHtml || email.body || '';
      if (!raw) return '';
      // Drop style/script before extracting text — otherwise the email's
      // own CSS leaks into the subject snippet (e.g. "body { margin: 0;
      // padding: 0; ..." from eAlert templates).
      let stripped: string;
      if (typeof window !== 'undefined' && email.bodyHtml) {
        const tmp = document.createElement('div');
        tmp.innerHTML = email.bodyHtml;
        tmp.querySelectorAll('style, script, noscript').forEach((n) => n.remove());
        stripped = (tmp.textContent || tmp.innerText || '').replace(/\s+/g, ' ').trim();
      } else {
        stripped = raw
          .replace(/<style[\s\S]*?<\/style>/gi, '')
          .replace(/<script[\s\S]*?<\/script>/gi, '')
          .replace(/<[^>]+>/g, '')
          .replace(/\s+/g, ' ')
          .trim();
      }
      return stripped.length > 80 ? stripped.slice(0, 80) + '…' : stripped;
    })() ||
    '(no subject)';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      className="h-[100dvh] md:h-[calc(100vh-1rem)] 3xl:h-[calc(100vh-3.5rem)]"
    >
      {/* Same header as the SMS, call and email thread modals: the lead's name, then
          their address. */}
      <Modal.Header>
        <ContactHeader
          name={leadName || null}
          subtitle={leadName ? leadEmail : null}
          contactId={contactId}
          onContactClick={
            onContactClick &&
            ((id) => {
              onClose();
              onContactClick(id);
            })
          }
          fallback={leadEmail || 'Email'}
        />
      </Modal.Header>
      <Modal.Body className="!overflow-hidden flex-1 min-h-0 flex flex-col">
        <div className="flex items-baseline justify-between gap-3 pb-3 min-w-0">
          <div className="text-[16px] font-medium text-foreground truncate">
            <span className="text-muted-foreground font-normal">Subject:</span> {subjectDisplay}
          </div>
          {sentAt && (
            <span className="text-xs text-muted-foreground tabular-nums whitespace-nowrap flex-shrink-0">
              {formatDate(sentAt, timezone, 'MMM D, YYYY h:mm A')}
            </span>
          )}
        </div>
        <div className="flex flex-col flex-1 min-h-0 rounded-md border border-foreground/10 overflow-hidden">
          {srcDoc ? (
            <iframe
              title="Email body"
              srcDoc={srcDoc}
              sandbox="allow-same-origin"
              className="w-full flex-1 min-h-0 border-0"
            />
          ) : (
            <div className="flex items-center justify-center flex-1 text-sm text-muted-foreground italic">
              No body content available for this email.
            </div>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};
